/**
 * Camera Settings Slice — toggle states for camera capture features.
 *
 * These flags drive watermark/capture behavior:
 * - includeFloorPlan: include the floor plan/arrow minimap in the watermark.
 * - autoMicEnabled: auto-start the mic after arrow placement.
 * - annotationModeEnabled: enter annotation mode after arrow placement.
 */

export interface CameraSettingsState {
  /** Include floor plan / arrow in the watermark. */
  includeFloorPlan: boolean;

  /** Auto-start mic after arrow placement. */
  autoMicEnabled: boolean;

  /** Enter annotation mode after arrow placement. */
  annotationModeEnabled: boolean;
}

export interface CameraSettingsActions {
  toggleFloorPlan: () => void;
  toggleAutoMic: () => void;
  toggleAnnotationMode: () => void;
  setIncludeFloorPlan: (value: boolean) => void;
  setAutoMicEnabled: (value: boolean) => void;
  setAnnotationModeEnabled: (value: boolean) => void;
}

export const cameraSettingsSlice: CameraSettingsState = {
  includeFloorPlan: true, // Default: include floor plan
  autoMicEnabled: false, // Default: don't auto-start mic
  annotationModeEnabled: false, // Default: no annotation mode
};

/**
 * Immer recipe type for this slice. Typed against the slice's own fields so we
 * can drop `any` while keeping the loose `set(fn)` signature the composed store
 * expects (full AppState typing here would create a circular reference).
 */
type CameraSettingsRecipe = (state: CameraSettingsState) => void;

export const cameraSettingsActions = (
  set: (fn: CameraSettingsRecipe) => void,
): CameraSettingsActions => ({
  toggleFloorPlan: () =>
    set((state) => {
      state.includeFloorPlan = !state.includeFloorPlan;
    }),

  toggleAutoMic: () =>
    set((state) => {
      state.autoMicEnabled = !state.autoMicEnabled;
    }),

  toggleAnnotationMode: () =>
    set((state) => {
      state.annotationModeEnabled = !state.annotationModeEnabled;
    }),

  setIncludeFloorPlan: (value: boolean) =>
    set((state) => {
      state.includeFloorPlan = value;
    }),

  setAutoMicEnabled: (value: boolean) =>
    set((state) => {
      state.autoMicEnabled = value;
    }),

  setAnnotationModeEnabled: (value: boolean) =>
    set((state) => {
      state.annotationModeEnabled = value;
    }),
});
